import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import { setAuthToken, setMsalInstance } from '../utils/api';

interface AuthenticationWrapperProps {
  children: React.ReactNode;
}

/**
 * Finishes redirect login, sets the active account, acquires a Microsoft Graph access token,
 * and attaches it to the shared axios client for @requires_auth API routes.
 */
const AuthenticationWrapper: React.FC<AuthenticationWrapperProps> = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Register MSAL instance for token refresh
    setMsalInstance(instance);

    if (inProgress !== 'none') {
      return;
    }

    let cancelled = false;

    const syncAuth = async () => {
      try {
        const redirectResult = await instance.handleRedirectPromise();
        const account =
          redirectResult?.account ??
          instance.getActiveAccount() ??
          instance.getAllAccounts()[0] ??
          null;

        if (redirectResult?.account) {
          instance.setActiveAccount(redirectResult.account);
        } else if (account) {
          instance.setActiveAccount(account);
        }

        if (!account) {
          if (!cancelled) {
            setAuthToken(null);
            setIsLoading(false);
          }
          return;
        }

        try {
          const tokenResponse = await instance.acquireTokenSilent({
            ...loginRequest,
            account,
          });
          if (!cancelled && tokenResponse?.accessToken) {
            setAuthToken(tokenResponse.accessToken);
          }
        } catch (silentErr) {
          console.warn('Silent token acquisition failed; trying interactive popup', silentErr);
          try {
            const tokenResponse = await instance.acquireTokenPopup({
              ...loginRequest,
              account,
            });
            if (!cancelled && tokenResponse?.accessToken) {
              setAuthToken(tokenResponse.accessToken);
            }
          } catch (popupErr) {
            console.error('Interactive token acquisition failed', popupErr);
            if (!cancelled) {
              setAuthToken(null);
            }
          }
        }
      } catch (e) {
        console.error('Authentication initialization error:', e);
        if (!cancelled) {
          setAuthToken(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    syncAuth();
    return () => {
      cancelled = true;
    };
  }, [instance, inProgress]);

  if (isLoading || inProgress !== 'none') {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontSize: '1.5rem',
        }}
      >
        <div>
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>🔐</div>
          <div>Authenticating...</div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthenticationWrapper;
